import { useEffect, useState } from 'react'
import { mdiCheck, mdiShieldAccountOutline } from '@mdi/js'
import { useTranslation } from 'react-i18next'
import type { CampaignEntry, CampaignItem } from '../../hooks/useCampaigns'
import { parseInput, sameValue, validateAnswer } from '../../lib/campaign-answer'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

/** The label of an item in the UI language. */
export function itemLabel(item: CampaignItem, lang: string): string {
    return item.label[lang] || item.label.fr || item.label.en || Object.values(item.label)[0] || item.property_code || ''
}

/** formatValue prints a stored value the way the agent reads it. */
export function formatValue(item: Pick<CampaignItem, 'data_type' | 'options'>, value: unknown, lang: string, yes: string, no: string): string {
    if (value === null || value === undefined) return ''
    if (typeof value === 'boolean') return value ? yes : no
    if (item.data_type === 'enum') {
        const label = (v: unknown) => {
            const option = item.options.find((o) => o.value === v)
            return option ? option.name[lang] || option.name.fr || option.name.en || option.value : String(v)
        }
        return Array.isArray(value) ? value.map(label).join(', ') : label(value)
    }
    if (item.data_type === 'date' && typeof value === 'string') {
        const [y, m, d] = value.split('-')
        return d && m && y ? `${d}/${m}/${y}` : value
    }
    if (typeof value === 'object') return JSON.stringify(value)
    return String(value)
}

/**
 * One surveyed value (patterns §48, survey field): the value known at the
 * launch, one gesture to confirm it, an input to correct it, checked against
 * the property type before it leaves. A choice or yes/no saves on click; a
 * typed value on Enter or blur.
 */
export function CampaignEntryField({
    entry,
    canFill,
    onSave,
    testId,
}: {
    entry: CampaignEntry
    canFill: boolean
    onSave: (answer: unknown, confirmed: boolean) => Promise<void>
    testId: string
}) {
    const { t, i18n } = useTranslation('campaigns')
    const lang = i18n.language.slice(0, 2)
    const item = entry.item
    const known = entry.known_value
    const answer = entry.answer
    const effective = answer ?? (entry.confirmed ? known : null)
    const [draft, setDraft] = useState(answer === null || answer === undefined ? '' : String(answer))
    const [error, setError] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        setDraft(answer === null || answer === undefined ? '' : String(answer))
    }, [answer])

    const save = async (value: unknown, confirmed: boolean) => {
        const problem = validateAnswer(item, value)
        if (problem) {
            setError(t(problem.key, problem.values))
            return
        }
        setError(null)
        setSaving(true)
        try {
            await onSave(value, confirmed)
        } catch (e) {
            setError((e as Error).message)
        } finally {
            setSaving(false)
        }
    }

    const saveTyped = () => {
        const value = parseInput(item.data_type, draft)
        if (sameValue(value, answer ?? null)) return
        void save(value, false)
    }

    const yes = t('yes')
    const no = t('no')
    const knownText = formatValue(item, known, lang, yes, no)
    const status = entry.confirmed && (answer === null || answer === undefined)
        ? 'confirmed'
        : answer !== null && answer !== undefined
          ? item.kind === 'question' ? 'answered' : sameValue(answer, known) ? 'confirmed' : 'corrected'
          : 'open'

    return (
        <div className="space-y-2 py-3" data-testid={testId} data-entry-status={status}>
            <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{itemLabel(item, lang)}</span>
                {item.required && (
                    <span className="text-destructive" title={t('required')} aria-label={t('required')}>*</span>
                )}
                {item.personal_data && (
                    <Badge variant="warning" className="gap-1" title={t('personal_data_hint')}>
                        <AppIcon path={mdiShieldAccountOutline} size="xs" />
                        {t('personal_data')}
                    </Badge>
                )}
                {status === 'confirmed' && (
                    <Badge variant="success" className="gap-1" data-testid={`${testId}-confirmed`}>
                        <AppIcon path={mdiCheck} size="xs" />
                        {t('entry.confirmed')}
                    </Badge>
                )}
                {(status === 'corrected' || status === 'answered') && (
                    <Badge variant="info" data-testid={`${testId}-${status}`}>{t(`entry.${status}`)}</Badge>
                )}
            </div>
            {item.kind === 'property' && (
                <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                    <span data-testid={`${testId}-known`}>
                        {knownText ? t('entry.known_value', { value: knownText }) : t('entry.known_none')}
                    </span>
                    {canFill && knownText && status === 'open' && (
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={saving}
                            onClick={() => void save(null, true)}
                            data-testid={`${testId}-confirm`}
                        >
                            <AppIcon path={mdiCheck} size="sm" className="mr-1" />
                            {t('entry.confirm')}
                        </Button>
                    )}
                </div>
            )}
            {canFill ? (
                <div className="flex flex-wrap items-center gap-2">
                    {item.data_type === 'boolean' ? (
                        <div className="flex gap-2" role="radiogroup">
                            {[true, false].map((v) => (
                                <Button
                                    key={String(v)}
                                    size="sm"
                                    role="radio"
                                    aria-checked={effective === v}
                                    variant={effective === v ? 'default' : 'outline'}
                                    disabled={saving}
                                    onClick={() => void save(v, false)}
                                    data-testid={`${testId}-${v ? 'yes' : 'no'}`}
                                >
                                    {v ? yes : no}
                                </Button>
                            ))}
                        </div>
                    ) : item.data_type === 'enum' && !item.multiple ? (
                        <Select
                            value={typeof effective === 'string' ? effective : ''}
                            onValueChange={(v) => void save(v, false)}
                            disabled={saving}
                        >
                            <SelectTrigger className="w-full sm:w-72" data-testid={`${testId}-select`}>
                                <SelectValue placeholder={t('entry.choose')} />
                            </SelectTrigger>
                            <SelectContent>
                                {item.options.map((o) => (
                                    <SelectItem key={o.value} value={o.value} data-testid={`${testId}-option-${o.value}`}>
                                        {o.name[lang] || o.name.fr || o.name.en || o.value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <Input
                            className="w-full sm:w-72"
                            type={item.data_type === 'date' ? 'date' : 'text'}
                            inputMode={item.data_type === 'number' ? 'decimal' : undefined}
                            value={draft}
                            placeholder={item.kind === 'property' && knownText ? knownText : t('entry.placeholder')}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={saveTyped}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    saveTyped()
                                }
                            }}
                            disabled={saving}
                            aria-invalid={!!error}
                            data-testid={`${testId}-input`}
                        />
                    )}
                    {(answer !== null && answer !== undefined) || entry.confirmed ? (
                        <Button
                            size="sm"
                            variant="ghost"
                            disabled={saving}
                            onClick={() => void save(null, false)}
                            data-testid={`${testId}-clear`}
                        >
                            {t('entry.clear')}
                        </Button>
                    ) : null}
                </div>
            ) : (
                <p className="text-sm" data-testid={`${testId}-answer`}>
                    {formatValue(item, effective, lang, yes, no) || <span className="text-muted-foreground">—</span>}
                </p>
            )}
            {error && (
                <p className="text-sm text-destructive" role="alert" data-testid={`${testId}-error`}>
                    {error}
                </p>
            )}
        </div>
    )
}
